import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { type SubmitWorkflowPayload, setSubmission, WorkflowError } from '../engine/workflowEngine'
import { deleteRow, findRow, insertRow, readTable, updateRow, writeTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { arrivalDetail, arrivalStages, pendingQcPackages } from './arrivalStage'
import { notify, userName, wmsRoute } from './notification.shared'
import { binsOfRm } from './rackScope'
import { assertNoTobacco, isTobaccoMaterialRow } from './tobaccoGuard'
import { toErrorResponse } from './workflow.handler'

/**
 * Endpoint custom C03.P04 Inbound Receiving (klasifikasi CUSTOM-NESTJS / WORKFLOW-CUSTOM) yang belum ada
 * backend-nya. Route mengikuti task FED M01.C03.P04.F01–F03; create penerimaan tembakau memakai route
 * BE task M01.C03.P04.F01 (`.../inbound-receiving/workflow/arrival-batch-log`, entity WF-TOB-01).
 * Nomor dokumen mengikuti prototype v7.44: kedatangan `IN-YYMMDD-nnn`, batch `{MAT}-{YYMMDD}-{NN}`,
 * kode unit/stiker `{MAT}:{YYYYMMDD}:{NN}:{nnnn}`, karantina `QRT-YYMMDD-nnn`, putaway `PWR-YYMMDD-nnn`,
 * penerimaan tembakau `TBM-YYMMDD-nnn`.
 */

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const ok = (data: unknown) => [200, { statusCode: 200, success: true, data }]

function fail(message: string, code = 'ERR_VALIDATION_FAILED', status = 400): never {
  throw new WorkflowError(message, code, status)
}

const pad = (value: number, width: number) => String(value).padStart(width, '0')

const ymd = (date = new Date()) => ({
  short: `${String(date.getFullYear()).slice(2)}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}`,
  long: `${date.getFullYear()}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}`
})

/** `PREFIX-YYMMDD-nnn`, urut per hari dari nilai yang sudah ada di kolom `field`. */
function nextDocNo(prefix: string, collection: string, field: string, date = new Date()) {
  const day = ymd(date).short
  const pattern = new RegExp(`^${prefix}-${day}-(\\d{3})`)
  const highest = readTable(collection)
    .map(row => pattern.exec(String(row[field] ?? '')))
    .reduce((max, match) => (match ? Math.max(max, Number(match[1])) : max), 0)
  return `${prefix}-${day}-${pad(highest + 1, 3)}`
}

/** Batch `{MAT}-{YYMMDD}-{NN}` — NN naik per material per hari dari seluruh batch yang tercatat. */
function nextBatchNo(materialCode: string, date = new Date()) {
  const day = ymd(date).short
  const prefix = `${materialCode.toUpperCase()}-${day}-`
  const used = [
    ...readTable('rm_arrival_line').map(row => row.batch_no),
    ...readTable('rm_tobacco_receipt_line').map(row => row.batch_no),
    ...readTable('rm_unit').map(row => row.batch_no)
  ]
    .map(value => String(value ?? ''))
    .filter(value => value.startsWith(prefix))
    .map(value => Number(value.slice(prefix.length)))
  return `${prefix}${pad(Math.max(0, ...used) + 1, 2)}`
}

const stickerOf = (batchNo: string, date = new Date()) => {
  const [code, , seq] = /^(.*)-(\d{6})-(\d{2})$/.exec(batchNo)?.slice(1) ?? [batchNo, '', '01']
  return `${code}:${ymd(date).long}:${seq}:0001`
}

/* ------------------------------------------------------------ akses */

function currentRoleCodes(): string[] {
  const userId = currentUserId()
  return readTable('mt_privileges')
    .filter(row => idOf(row.user) === userId)
    .map(row => String(findRow('mt_roles', idOf(row.role))?.code ?? ''))
}

function requireRole(...roles: string[]) {
  if (!currentRoleCodes().some(code => roles.includes(code)))
    fail('Hanya Admin WH RM yang boleh menjalankan aksi ini.', 'ERR_UNAUTHORIZED_ROLE', 403)
}

/* ------------------------------------------------------- kedatangan */

type ArrivalBody = {
  sj_no?: string
  supplier_id?: string
  po_groups?: { po_no?: string; lines?: { material_id?: string; qty?: number }[] }[]
}

export function createArrival(body: ArrivalBody) {
  requireRole('admin_wh_rm')
  const sjNo = String(body.sj_no ?? '').trim()
  if (!sjNo) fail('No. Surat Jalan wajib diisi.')
  const supplier = findRow('mt_companies', body.supplier_id)
  if (!supplier) fail('Supplier Pengirim wajib dipilih.')
  const groups = body.po_groups ?? []
  if (!groups.length) fail('Minimal satu Nomor PO wajib diisi.')

  /** D97: Tembakau tidak pernah tiba sebagai kedatangan supplier/PO — hanya lewat Penerimaan Tembakau. */
  assertNoTobacco(groups.flatMap(group => (group.lines ?? []).map(line => line.material_id)))

  const packs = readTable('mt_material_pack_standard').filter(
    pack => idOf(pack.supplier_id) === body.supplier_id && pack.status !== 'archived'
  )
  const lines = groups.flatMap(group => {
    const poNo = String(group.po_no ?? '').trim()
    if (!poNo) fail('Setiap grup wajib mengisi Nomor PO.')
    if (!group.lines?.length) fail(`Grup ${poNo}: minimal satu baris material diperlukan.`)
    return (group.lines ?? []).map(line => {
      const material = findRow('mt_material', line.material_id)
      if (!material || !(Number(line.qty) > 0)) fail('Setiap baris wajib memilih Material dan Qty lebih dari 0.')
      const pack = packs.find(row => idOf(row.material_id) === line.material_id)
      /** BIR-ARR-01 — satu Surat Jalan dari satu supplier: material harus dipasok supplier tersebut. */
      if (!pack) fail(`${material?.code} tidak dipasok ${supplier?.name}.`)
      return { poNo, material: material as LocalRow, qty: Number(line.qty), unitId: idOf(pack?.unit_big_id) }
    })
  })

  const now = new Date()
  const arrival = insertRow('rm_arrival', {
    ...auditFieldsForCreate(),
    id: newId(),
    arrival_no: nextDocNo('IN', 'rm_arrival', 'arrival_no', now),
    sj_no: sjNo,
    supplier_id: body.supplier_id,
    received_at: now.toISOString()
  })
  const inserted = lines.map(line =>
    insertRow('rm_arrival_line', {
      ...auditFieldsForCreate(),
      id: newId(),
      arrival_id: arrival.id,
      po_no: line.poNo,
      material_id: line.material.id,
      qty: line.qty,
      unit_id: line.unitId || idOf(line.material.default_purchase_unit_id) || null,
      batch_no: nextBatchNo(String(line.material.code), now),
      line_status: 'WAITING_PALLETIZING',
      is_over_delivery: false,
      over_delivery_qty: 0
    })
  )
  return {
    id: arrival.id,
    arrival_no: arrival.arrival_no,
    sj_no: arrival.sj_no,
    batch_count: inserted.length,
    batches: inserted.map(line => ({ id: line.id, batch_no: line.batch_no, po_no: line.po_no, qty: line.qty }))
  }
}

/* -------------------------------------------------------- palletizing */

const contentsOfLine = (lineId: string) =>
  readTable('rm_pallet_content').filter(content => idOf(content.arrival_line_id) === lineId)

/** Sisa qty batch yang belum masuk pallet (status PALLETIZED = habis). */
export function remainingOfLine(line: LocalRow) {
  if (line.line_status === 'PALLETIZED') return 0
  return Number(line.qty) - contentsOfLine(line.id).reduce((sum, content) => sum + Number(content.qty), 0)
}

function syncLineStatus(lineId: string) {
  const line = findRow('rm_arrival_line', lineId)
  if (!line) return
  const assigned = contentsOfLine(lineId).reduce((sum, content) => sum + Number(content.qty), 0)
  updateRow('rm_arrival_line', lineId, {
    line_status: assigned >= Number(line.qty) ? 'PALLETIZED' : 'WAITING_PALLETIZING',
    ...auditFieldsForUpdate()
  })
}

/** BIR-PLT-01: pallet karantina yang QC-nya sudah dijadwalkan dikunci. */
function isPalletLocked(palletId: string) {
  const entries = readTable('rm_quarantine_entry').filter(
    entry => idOf(entry.pallet_id) === palletId && entry.is_qc_completed !== true
  )
  return readTable('rm_qc_assignment').some(assignment =>
    entries.some(entry => entry.id === idOf(assignment.quarantine_entry_id))
  )
}

function requirePallet(palletId: unknown) {
  const pallet = findRow('pallet', palletId)
  if (!pallet || pallet.is_active === false) fail('QR Pallet tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return pallet as LocalRow
}

function addContent(palletId: string, lineId: string, qty: number) {
  const existing = readTable('rm_pallet_content').find(
    content => idOf(content.pallet_id) === palletId && idOf(content.arrival_line_id) === lineId
  )
  if (existing)
    updateRow('rm_pallet_content', existing.id, { qty: Number(existing.qty) + qty, ...auditFieldsForUpdate() })
  else
    insertRow('rm_pallet_content', {
      ...auditFieldsForCreate(),
      id: newId(),
      pallet_id: palletId,
      arrival_line_id: lineId,
      qty
    })
}

function upsertQuarantine(palletId: string, lineId: string, qty: number) {
  const entry = readTable('rm_quarantine_entry').find(
    row => idOf(row.pallet_id) === palletId && idOf(row.arrival_line_id) === lineId && row.is_qc_completed !== true
  )
  if (entry) {
    updateRow('rm_quarantine_entry', entry.id, { qty: Number(entry.qty) + qty, ...auditFieldsForUpdate() })
    return String(entry.quarantine_no)
  }
  const created = insertRow('rm_quarantine_entry', {
    ...auditFieldsForCreate(),
    id: newId(),
    quarantine_no: nextDocNo('QRT', 'rm_quarantine_entry', 'quarantine_no'),
    pallet_id: palletId,
    arrival_line_id: lineId,
    qty,
    quarantine_status: 'IN_QUARANTINE',
    qty_good: 0,
    qty_ng: 0,
    is_qc_completed: false,
    entered_at: new Date().toISOString(),
    qc_completed_at: null,
    warning_level: null
  })
  return String(created.quarantine_no)
}

type AssignBody = { pallet_id?: string; items?: { arrival_line_id?: string; qty?: number }[] }

export function assignToPallet(body: AssignBody) {
  requireRole('admin_wh_rm')
  const pallet = requirePallet(body.pallet_id)
  if (pallet.pallet_status === 'IN_QC' && isPalletLocked(pallet.id))
    fail('QC pallet ini sudah dijadwalkan — isinya tidak bisa ditambah lagi.', 'ERR_RESOURCE_LOCKED', 423)
  const items = body.items ?? []
  if (!items.length) fail('Centang minimal satu batch dulu.')
  const moves = items.map(item => {
    const line = findRow('rm_arrival_line', item.arrival_line_id)
    if (!line) fail('Batch tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    const remaining = remainingOfLine(line as LocalRow)
    const qty = Number(item.qty)
    if (!(qty >= 1 && qty <= remaining)) fail(`Qty ${line?.batch_no} harus antara 1 dan ${remaining}.`)
    return { line: line as LocalRow, qty }
  })
  const inQc = pallet.pallet_status === 'IN_QC'
  moves.forEach(({ line, qty }) => {
    addContent(pallet.id, line.id, qty)
    syncLineStatus(line.id)
    if (inQc) upsertQuarantine(pallet.id, line.id, qty)
  })
  updateRow('pallet', pallet.id, auditFieldsForUpdate())
  return { pallet_id: pallet.id, pallet_code: pallet.code, batch_count: moves.length, in_qc: inQc }
}

type ContentBody = { pallet_content_id?: string; qty?: number; to_pallet_id?: string }

function requireEditableContent(body: ContentBody) {
  const content = findRow('rm_pallet_content', body.pallet_content_id)
  if (!content) fail('Isi pallet tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const pallet = requirePallet(idOf(content?.pallet_id))
  if (pallet.pallet_status === 'IN_QC')
    fail('Pallet sudah di Karantina — isinya tidak bisa diubah dari sini.', 'ERR_RESOURCE_LOCKED', 423)
  const qty = Number(body.qty)
  if (!(qty >= 1 && qty <= Number(content?.qty))) fail(`Jumlah harus antara 1 dan ${content?.qty}.`)
  return { content: content as LocalRow, pallet, qty }
}

function takeFromContent(content: LocalRow, qty: number) {
  if (qty >= Number(content.qty)) deleteRow('rm_pallet_content', content.id)
  else updateRow('rm_pallet_content', content.id, { qty: Number(content.qty) - qty, ...auditFieldsForUpdate() })
}

export function removeFromPallet(body: ContentBody) {
  requireRole('admin_wh_rm')
  const { content, pallet, qty } = requireEditableContent(body)
  takeFromContent(content, qty)
  syncLineStatus(idOf(content.arrival_line_id))
  return { pallet_id: pallet.id, pallet_code: pallet.code, qty }
}

export function moveBetweenPallets(body: ContentBody) {
  requireRole('admin_wh_rm')
  const { content, pallet, qty } = requireEditableContent(body)
  const target = requirePallet(body.to_pallet_id)
  if (target.id === pallet.id) fail('Pilih pallet tujuan yang berbeda.')
  if (target.pallet_status === 'IN_QC')
    fail('Pallet tujuan sudah di Karantina — pilih pallet yang belum diselesaikan.', 'ERR_RESOURCE_LOCKED', 423)
  takeFromContent(content, qty)
  addContent(target.id, idOf(content.arrival_line_id), qty)
  return { from_pallet_code: pallet.code, to_pallet_id: target.id, to_pallet_code: target.code, qty }
}

export function closePallet(body: { pallet_id?: string }) {
  requireRole('admin_wh_rm')
  const pallet = requirePallet(body.pallet_id)
  if (pallet.pallet_status === 'IN_QC') fail('Pallet sudah di Karantina.', 'ERR_RESOURCE_LOCKED', 423)
  const contents = readTable('rm_pallet_content').filter(content => idOf(content.pallet_id) === pallet.id)
  if (!contents.length) fail('Pallet masih kosong — assign batch dulu.')
  /** Satu baris karantina per batch di atas pallet (BA JAL-PG-40-025 §19, ERD `arrival_line_id`). */
  const quarantineNos = contents.map(content =>
    upsertQuarantine(pallet.id, idOf(content.arrival_line_id), Number(content.qty))
  )
  updateRow('pallet', pallet.id, { pallet_status: 'IN_QC', ...auditFieldsForUpdate() })
  /** BA 17B §5: pallet kedatangan ditutup (masuk karantina) → Admin WH RM menjadwalkan QC. */
  notify({
    to: { roles: ['admin_wh_rm'] },
    type: 'ARRIVAL_PALLET_CLOSED',
    title: `Pallet ${pallet.code} masuk karantina`,
    body: `Pallet ${pallet.code} ditutup (${quarantineNos.length} batch) — jadwalkan QC barang karantina.`,
    link: wmsRoute('inbound-rm/qc-scheduler'),
    ref: { type: 'pallet', id: String(pallet.id) }
  })
  return { pallet_id: pallet.id, pallet_code: pallet.code, quarantine_nos: quarantineNos }
}

/* ---------------------------------------------------- tembakau */

const TOBACCO_CATEGORY = 'TEMBAKAU'

const isTobacco = isTobaccoMaterialRow

type TobaccoLineInput = { material_id?: string; qty_sak?: number }

export function createTobaccoReceiptAndSubmit(body: SubmitWorkflowPayload) {
  const payload = (body.native_document_payload?.payload ?? {}) as { lines?: TobaccoLineInput[]; remark?: string }
  const lines = payload.lines ?? []
  if (!lines.length) fail('Minimal satu jenis tembakau.')
  const seen = new Set<string>()
  lines.forEach(line => {
    const material = findRow('mt_material', line.material_id)
    if (!material || !isTobacco(material)) fail('Masih ada baris tanpa jenis tembakau.')
    if (!(Number(line.qty_sak) > 0)) fail('Jumlah Sak harus lebih dari nol.')
    if (seen.has(String(line.material_id))) fail(`Jenis ${material?.code} ditulis dua kali — gabungkan jumlahnya.`)
    seen.add(String(line.material_id))
  })

  const receiptId = newId()
  insertRow('rm_tobacco_receipt', {
    ...auditFieldsForCreate(),
    id: receiptId,
    receipt_no: nextDocNo('TBM', 'rm_tobacco_receipt', 'receipt_no'),
    supplier_id: null,
    status_id: null,
    submission_id: null,
    remark: String(payload.remark ?? '').trim() || null
  })
  const inserted = lines.map(line =>
    insertRow('rm_tobacco_receipt_line', {
      ...auditFieldsForCreate(),
      id: newId(),
      receipt_id: receiptId,
      material_id: line.material_id,
      qty_sak: Number(line.qty_sak),
      batch_no: null,
      sticker_qr: null
    })
  )
  try {
    return setSubmission({
      ...body,
      submission_id: null,
      entity_id: receiptId,
      native_document_payload: { path: 'rm_tobacco_receipt', payload: { id: receiptId } }
    })
  } catch (error) {
    inserted.forEach(row => deleteRow('rm_tobacco_receipt_line', row.id))
    deleteRow('rm_tobacco_receipt', receiptId)
    throw error
  }
}

/** Rak paling lengang yang boleh menampung tembakau (kategori Tembakau atau tanpa batasan) — prototype `tbkRakUsul`. */
export function recommendedTobaccoRack() {
  const tobaccoGroup = readTable('mt_material_group').find(
    group => String(group.code).toUpperCase() === TOBACCO_CATEGORY
  )
  const racks = new Map(readTable('mt_rack').map(rack => [rack.id, rack]))
  const occupancy = new Map<string, number>()
  readTable('rm_unit')
    .filter(unit => unit.unit_status === 'IN_STOCK' && unit.rack_bin_id)
    .forEach(unit => occupancy.set(idOf(unit.rack_bin_id), (occupancy.get(idOf(unit.rack_bin_id)) ?? 0) + 1))
  const bins = binsOfRm()
    .filter(bin => {
      const rack = racks.get(idOf(bin.rack_id))
      const restriction = idOf(rack?.category_restriction_id)
      return bin.is_active !== false && (!restriction || restriction === tobaccoGroup?.id)
    })
    .sort(
      (a, b) => (occupancy.get(a.id) ?? 0) - (occupancy.get(b.id) ?? 0) || String(a.code).localeCompare(String(b.code))
    )
  const bin = bins[0]
  return bin ? { rack_bin_id: bin.id, code: bin.code } : null
}

type AssignPutawayBody = {
  receipt_id?: string
  lines?: { receipt_line_id?: string; assignments?: { operator_id?: string; qty?: number }[] }[]
}

export function assignTobaccoPutaway(body: AssignPutawayBody) {
  requireRole('admin_wh_rm')
  const receipt = findRow('rm_tobacco_receipt', body.receipt_id)
  if (!receipt) fail('Penerimaan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const status = findRow('wml_status', idOf(receipt?.status_id))
  if (status?.code !== 'TOB_REC_APPROVED')
    fail('Status dokumen berubah, refresh halaman.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const rack = recommendedTobaccoRack()
  if (!rack) fail('Belum ada rak yang boleh menampung Tembakau di Master Lokasi.')
  const operatorRole = readTable('mt_roles').find(role => role.code === 'operator_wh_rm')
  const operators = new Set(
    readTable('mt_privileges')
      .filter(row => idOf(row.role) === operatorRole?.id)
      .map(row => idOf(row.user))
  )

  const plan = (body.lines ?? []).map(input => {
    const line = findRow('rm_tobacco_receipt_line', input.receipt_line_id)
    if (!line || idOf(line.receipt_id) !== receipt?.id) fail('Baris penerimaan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    if (line?.batch_no) fail('Stiker baris ini sudah terbit.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
    const assignments = input.assignments ?? []
    if (!assignments.length || assignments.some(part => !part.operator_id)) fail('Masih ada baris tanpa petugas.')
    if (assignments.some(part => !operators.has(String(part.operator_id))))
      fail('Petugas harus Operator WH RM.', 'ERR_VALIDATION_FAILED')
    if (assignments.some(part => !(Number(part.qty) > 0))) fail('Qty harus lebih dari 0.')
    const total = assignments.reduce((sum, part) => sum + Number(part.qty), 0)
    /** BIR-TBC-01 — pembagian harus pas dengan jumlah datang. */
    if (total !== Number(line?.qty_sak)) fail(`Pembagian harus pas ${line?.qty_sak} Sak (terbagi ${total}).`)
    return { line: line as LocalRow, assignments }
  })
  const pending = readTable('rm_tobacco_receipt_line').filter(
    line => idOf(line.receipt_id) === receipt?.id && !line.batch_no
  )
  if (!plan.length || pending.some(line => !plan.some(item => item.line.id === line.id)))
    fail('Bagi tugas untuk setiap jenis tembakau.')

  const now = new Date()
  const taskNo = nextDocNo('PWR', 'rm_putaway_task', 'task_no', now)
  const units: LocalRow[] = []
  const tasks: LocalRow[] = []
  const items: LocalRow[] = []
  let uidNo = readTable('rm_unit').length
  const issued = plan.map(({ line, assignments }) => {
    const material = findRow('mt_material', idOf(line.material_id)) as LocalRow
    const perSak = Number(material.default_purchase_unit_qty ?? 1)
    const batchNo = nextBatchNo(String(material.code), now)
    const first = stickerOf(batchNo, now)
    const prefix = first.slice(0, first.lastIndexOf(':') + 1)
    /*
     * Keputusan user 2026-10-21 #1 (usulan, menunggu konfirmasi akhir): stok tembakau dicatat PER LABEL di posisi rak.
     * Satu Sak = satu label = satu `rm_unit` (kode unit ke-0001 … ke-N dalam satu batch); satu label tidak pernah
     * dipecah ke dua rak. Sebelumnya satu label menutup seluruh tumpukan (prototype: satu kode dicetak N lembar).
     */
    const labels = Array.from({ length: Number(line.qty_sak) }, (_, index) => {
      uidNo += 1
      return {
        ...auditFieldsForCreate(),
        id: newId(),
        uid: `UNIT-${pad(uidNo, 6)}`,
        qr_code: `${prefix}${pad(index + 1, 4)}`,
        material_id: material.id,
        batch_no: batchNo,
        supplier_id: null,
        po_no: null,
        qty_small: perSak,
        rack_bin_id: null,
        flag: 'REGULAR',
        unit_status: 'AWAITING_PUTAWAY',
        unit_big_snapshot: 'Sak',
        small_per_big_snapshot: perSak,
        is_leftover_wip: false,
        /** ERD-ADD (laporan 32): label tembakau → baris penerimaannya. */
        tobacco_receipt_line_id: line.id
      } as LocalRow
    })
    units.push(...labels)
    updateRow('rm_tobacco_receipt_line', line.id, { batch_no: batchNo, sticker_qr: first, ...auditFieldsForUpdate() })
    let cursor = 0
    assignments.forEach(part => {
      const task = {
        ...auditFieldsForCreate(),
        id: newId(),
        task_no: taskNo,
        operator_id: part.operator_id,
        task_status: 'ASSIGNED',
        /** v7.50 R-01: tahap 1 tembakau = scan stiker barang yang dibawa (tanpa gerbang Area QC RM). */
        phase: 'PICK',
        note: null
      } as LocalRow
      tasks.push(task)
      /* Bagian petugas = label berurutan: satu item tugas per label (qty 1 Sak). */
      labels.slice(cursor, cursor + Number(part.qty)).forEach(unit =>
        items.push({
          ...auditFieldsForCreate(),
          id: newId(),
          task_id: task.id,
          unit_id: unit.id,
          recommended_rack_bin_id: rack?.rack_bin_id,
          recommended_warehouse_zone_id: null,
          actual_rack_bin_id: null,
          actual_warehouse_zone_id: null,
          qty_assigned: 1,
          qty_placed: 0,
          qty_short: null,
          picked_at: null,
          override_reason: null
        })
      )
      cursor += Number(part.qty)
    })
    return {
      receipt_line_id: line.id,
      batch_no: batchNo,
      sticker_qr: first,
      labels: labels.map(unit => String(unit.qr_code))
    }
  })
  writeTable('rm_unit', [...readTable('rm_unit'), ...units])
  writeTable('rm_putaway_task', [...readTable('rm_putaway_task'), ...tasks])
  writeTable('rm_putaway_task_item', [...readTable('rm_putaway_task_item'), ...items])
  updateRow('rm_tobacco_receipt', String(receipt?.id), auditFieldsForUpdate())
  /** Putaway ditugaskan → tiap operator mendapat satu notifikasi berisi jumlah Sak bagiannya. */
  const perOperator = new Map<string, number>()
  plan.forEach(({ assignments }) =>
    assignments.forEach(part =>
      perOperator.set(String(part.operator_id), (perOperator.get(String(part.operator_id)) ?? 0) + Number(part.qty))
    )
  )
  perOperator.forEach((sak, operatorId) =>
    notify({
      to: { userIds: [operatorId] },
      type: 'PUTAWAY_ASSIGNED',
      title: `Tugas putaway ${taskNo} untuk Anda`,
      body: `${userName(currentUserId())} menugaskan putaway tembakau ${sak} Sak ke ${rack?.code ?? 'rak rekomendasi'} — buka Putaway RM — Operator.`,
      link: wmsRoute('inbound-rm/putaway-rm-operator'),
      ref: { type: 'rm_tobacco_receipt', id: String(receipt?.id) }
    })
  )
  return { receipt_id: receipt?.id, task_no: taskNo, recommended_rack: rack?.code, lines: issued }
}

/* ------------------------------------------------------------ routes */

const parse = <T>(data: unknown) => JSON.parse(String(data ?? '{}')) as T

const handle = (run: () => unknown) => {
  try {
    return ok(run())
  } catch (error) {
    return toErrorResponse(error)
  }
}

export function registerInboundHandlers(mock: MockAdapter) {
  mock.onPost('/api/v1/wms/arrivals/create').reply(config => handle(() => createArrival(parse(config.data))))
  /* v7.50 PRC-05: halaman Detail Kedatangan, chip tahap daftar, pengingat Admin (laporan 23 R-10 / U-01 / U-02). */
  mock.onGet('/api/v1/wms/inbound-receiving/arrival-detail').reply(config =>
    handle(() => {
      const detail = arrivalDetail(String(config.params?.arrival_no ?? ''))
      if (!detail) fail('Kedatangan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
      return detail
    })
  )
  mock.onGet('/api/v1/wms/inbound-receiving/arrival-stage').reply(config =>
    handle(() =>
      arrivalStages(
        String(config.params?.ids ?? '')
          .split(',')
          .filter(Boolean)
      )
    )
  )
  mock.onGet('/api/v1/wms/inbound-receiving/qc-package/pending').reply(() => handle(() => pendingQcPackages()))
  mock.onPost('/api/v1/wms/pallets/assign').reply(config => handle(() => assignToPallet(parse(config.data))))
  mock.onPost('/api/v1/wms/pallets/remove').reply(config => handle(() => removeFromPallet(parse(config.data))))
  mock.onPost('/api/v1/wms/pallets/move').reply(config => handle(() => moveBetweenPallets(parse(config.data))))
  mock.onPost('/api/v1/wms/pallets/close').reply(config => handle(() => closePallet(parse(config.data))))
  mock
    .onPost('/api/v1/wms/inbound-receiving/workflow/arrival-batch-log')
    .reply(config => handle(() => createTobaccoReceiptAndSubmit(parse(config.data))))
  mock.onGet('/api/v1/wms/tobacco/recommended-rack').reply(() => handle(() => recommendedTobaccoRack()))
  mock
    .onPost('/api/v1/wms/tobacco/assign-putaway')
    .reply(config => handle(() => assignTobaccoPutaway(parse(config.data))))
}
